import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import axios from "axios";
import {
  ArrowLeft,
  DollarSign,
  Users,
  MessageCircle,
  Sailboat,
  Loader2,
  Activity,
  Calendar,
  AlertTriangle,
  ShieldCheck,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso.slice(0, 10);
  }
}

function daysSince(iso) {
  if (!iso) return null;
  try {
    const ms = Date.now() - new Date(iso).getTime();
    return Math.floor(ms / 86400000);
  } catch {
    return null;
  }
}

export default function AdminUsagePage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState(30);

  useEffect(() => {
    loadData(period);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  const loadData = async (days) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("odys_token");
      const res = await axios.get(
        `${API}/api/admin/usage-by-user?period_days=${days}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setData(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading && !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--paper-2)]">
        <Loader2 className="w-8 h-8 animate-spin text-[var(--gold)]" />
      </div>
    );
  }

  const totalCostEur = data ? (data.total_cost_eur ?? data.total_cost_usd * 0.92).toFixed(2) : "0";
  const avgCostPerActive =
    data && data.active_users > 0
      ? (data.total_cost_usd / data.active_users).toFixed(2)
      : "0";

  return (
    <div className="min-h-screen bg-[var(--paper-2)]">
      {/* Header */}
      <header className="bg-[var(--paper)] border-b border-[var(--line)] sticky top-0 z-20 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between gap-4">
          <button
            onClick={() => navigate("/admin")}
            data-testid="back-to-admin-btn"
            className="flex items-center gap-2 text-sm font-medium text-[var(--ink)] hover:opacity-70 transition-opacity"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour au panel admin
          </button>
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[var(--gold)]">
            <Activity className="w-3.5 h-3.5" />
            Usage & couts
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-8 py-8 md:py-12">
        {/* Title */}
        <div className="mb-8">
          <div className="text-[0.7rem] uppercase tracking-[0.28em] font-semibold text-[var(--gold)] mb-2">
            Dashboard administrateur
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-[var(--ink)] mb-3" style={{ fontFamily: "var(--serif)" }}>
            Usage IA & couts par eleve
          </h1>
          <p className="text-sm text-[var(--ink-soft)] max-w-2xl leading-relaxed">
            Visualise l'utilisation de chaque eleve sur Odys'IA et le cout IA
            estime en euros. Base de calcul : 0,025 $ par message chat, 0,50 $
            par Reunion d'equipage, et le cout reel mesure de chaque analyse
            renforcee (cache mutualise deja deduit).
          </p>
        </div>

        {/* Period filter */}
        <div className="mb-6 flex items-center gap-2 flex-wrap">
          <Calendar className="w-4 h-4 text-[var(--muted)]" />
          <span className="text-xs uppercase tracking-[0.2em] text-[var(--muted)] font-semibold mr-2">
            Periode :
          </span>
          {[7, 30, 90, 365].map((d) => (
            <button
              key={d}
              onClick={() => setPeriod(d)}
              data-testid={`period-${d}d`}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
                period === d
                  ? "bg-[var(--ink)] text-white"
                  : "bg-[var(--paper)] text-[var(--ink-soft)] border border-[var(--line)] hover:border-[var(--gold)]"
              }`}
            >
              {d === 7 ? "7 jours" : d === 30 ? "30 jours" : d === 90 ? "3 mois" : "1 an"}
            </button>
          ))}
        </div>

        {/* KPI cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <KpiCard
            label="Eleves actifs"
            value={`${data?.active_users || 0} / ${data?.total_students || 0}`}
            icon={<Users className="w-4 h-4" />}
            sub={`${data?.total_students || 0} comptes au total`}
          />
          <KpiCard
            label="Messages chat"
            value={data?.total_messages || 0}
            icon={<MessageCircle className="w-4 h-4" />}
            sub={`x $${data?.cost_per_message_usd || 0.025}`}
          />
          <KpiCard
            label="Analyses renforcees"
            value={data?.total_deep_analyses || 0}
            icon={<Sailboat className="w-4 h-4" />}
            sub={`${data?.total_roundtables || 0} reunions d'equipage`}
          />
          <KpiCard
            label="Cout total estime"
            value={`${totalCostEur} EUR`}
            icon={<DollarSign className="w-4 h-4" />}
            sub={`${data?.avg_cost_per_active_user_eur ?? 0} EUR par eleve actif`}
            highlight
          />
        </div>

        {/* Table */}
        <Card className="bg-[var(--paper)] border-[var(--line)]">
          <CardHeader className="border-b border-[var(--line)] pb-4">
            <CardTitle className="text-base font-bold text-[var(--ink)]" style={{ fontFamily: "var(--serif)" }}>
              Detail par eleve
            </CardTitle>
            <p className="text-xs text-[var(--muted)] mt-1">
              Trie par cout decroissant (les plus actifs en haut)
            </p>
          </CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full" data-testid="usage-table">
              <thead>
                <tr className="border-b border-[var(--line)] text-left text-[0.7rem] uppercase tracking-[0.14em] font-semibold text-[var(--muted)]">
                  <th className="px-4 py-3">Eleve</th>
                  <th className="px-4 py-3 text-right">Conversations</th>
                  <th className="px-4 py-3 text-right">Messages</th>
                  <th className="px-4 py-3 text-right">Reunions d'eq.</th>
                  <th className="px-4 py-3 text-right">Analyses renf.</th>
                  <th className="px-4 py-3 text-right">Cout estime</th>
                  <th className="px-4 py-3">Derniere activite</th>
                </tr>
              </thead>
              <tbody>
                {data && data.rows && data.rows.length > 0 ? (
                  data.rows.map((row) => {
                    const days = daysSince(row.last_activity);
                    const inactive = days !== null && days > 14;
                    const veryActive = row.estimated_cost_usd > 2;
                    return (
                      <tr
                        key={row.student_id}
                        data-testid={`user-row-${row.student_id}`}
                        className="border-b border-[var(--line)] hover:bg-[var(--gold-bg)] transition-colors"
                      >
                        <td className="px-4 py-3">
                          <div className="font-semibold text-sm text-[var(--ink)]">
                            {row.student_name}
                          </div>
                          {row.email && (
                            <div className="text-xs text-[var(--muted)]">{row.email}</div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right text-sm font-medium text-[var(--ink)]">
                          {row.conversations}
                        </td>
                        <td className="px-4 py-3 text-right text-sm font-medium text-[var(--ink)]">
                          {row.messages}
                        </td>
                        <td className="px-4 py-3 text-right text-sm font-medium text-[var(--ink)]">
                          {row.roundtables > 0 ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[var(--gold-bg)] text-[var(--gold-dark)] text-xs font-semibold border border-[var(--gold-soft)]">
                              <Sailboat className="w-3 h-3" />
                              {row.roundtables}
                            </span>
                          ) : (
                            <span className="text-[var(--muted)]">0</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right text-sm font-medium text-[var(--ink)]" data-testid={`user-deep-${row.student_id}`}>
                          {row.deep_analyses > 0 ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[var(--gold-bg)] text-[var(--gold-dark)] text-xs font-semibold border border-[var(--gold-soft)]">
                              <ShieldCheck className="w-3 h-3" />
                              {row.deep_analyses}
                            </span>
                          ) : (
                            <span className="text-[var(--muted)]">0</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right" data-testid={`user-cost-${row.student_id}`}>
                          <span
                            className={`text-sm font-bold ${veryActive ? "text-[var(--gold-dark)]" : "text-[var(--ink)]"}`}
                          >
                            {(row.estimated_cost_eur ?? row.estimated_cost_usd * 0.92).toFixed(2)} EUR
                          </span>
                        </td>
                        <td className="px-4 py-3 text-xs">
                          {row.last_activity ? (
                            <span
                              className={`flex items-center gap-1.5 ${inactive ? "text-[var(--muted)]" : "text-[var(--ink)]"}`}
                            >
                              {inactive && (
                                <AlertTriangle className="w-3 h-3 text-amber-600" />
                              )}
                              {formatDate(row.last_activity)}
                              {days !== null && (
                                <span className="text-[var(--muted)]">
                                  · il y a {days}j
                                </span>
                              )}
                            </span>
                          ) : (
                            <span className="text-[var(--muted)] italic">
                              Jamais utilise
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td
                      colSpan={7}
                      className="px-4 py-8 text-center text-sm text-[var(--muted)]"
                    >
                      Aucun eleve sur cette periode.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>

        {/* Footer note */}
        <div className="mt-8 p-4 rounded-md bg-[var(--gold-bg)] border-l-4 border-[var(--gold)] text-xs text-[var(--ink-soft)] leading-relaxed">
          <strong>Note sur les estimations :</strong> les couts sont indicatifs
          et calcules sur une moyenne d'usage typique (500 tokens input + 1500
          tokens output par message, 3-4 experts Sonnet + verdict Opus par
          Reunion d'equipage). Le cout reel peut varier de ±30% selon la
          longueur des conversations. Consulte ton tableau de bord Universal Key
          dans ton profil Emergent pour le cout reel facture.
        </div>
      </main>
    </div>
  );
}

function KpiCard({ label, value, icon, sub, highlight }) {
  return (
    <Card
      className={`bg-[var(--paper)] border-[var(--line)] relative overflow-hidden ${highlight ? "border-[var(--gold)]" : ""}`}
    >
      {highlight && (
        <span
          aria-hidden="true"
          className="absolute top-0 left-0 right-0 h-[3px] bg-[var(--gold)]"
        />
      )}
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-2 text-[var(--gold)]">
          {icon}
          <span className="text-[0.66rem] uppercase tracking-[0.22em] font-semibold">
            {label}
          </span>
        </div>
        <div
          className="text-2xl md:text-3xl font-bold text-[var(--ink)] mb-1"
          style={{ fontFamily: "var(--serif)" }}
        >
          {value}
        </div>
        {sub && (
          <div className="text-[0.7rem] text-[var(--muted)]">{sub}</div>
        )}
      </CardContent>
    </Card>
  );
}
